import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../common/design-system/Button';
import CreatorPerformanceCard from './CreatorPerformanceCard';
import { getCreatorEmvReport } from '../../services/api';
import styles from './CreatorEmvReport.module.scss';

const CreatorEmvReport = () => {
  const { t, i18n } = useTranslation('creator');
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCreatorEmvReport(id);
      setReport(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || t('emv_report.load_error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const goBack = () => navigate(`/creator/collabs/${id}`);
  const reportMeta = report?.report || {};
  const isSnapshot = Boolean(reportMeta.is_snapshot);
  const locale = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt-BR'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en-US'
      : 'es-PA';
  const updatedAt = reportMeta.finalized_at || reportMeta.generated_at;
  const formattedUpdatedAt = updatedAt
    ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(updatedAt))
    : null;

  return (
    <main className={styles.page}>
      <div className={styles.container}>
        <Button variant="ghost" size="sm" className={styles.backButton} onClick={goBack}>
          <ArrowLeft size={16} /> {t('emv_report.back')}
        </Button>

        <header className={styles.hero}>
          <div className={styles.kicker}>
            <Sparkles size={15} aria-hidden="true" />
            {t('emv_report.kicker')}
          </div>
          <div className={styles.heroRow}>
            <div>
              <h1>{report?.offer?.title || t('emv_report.title')}</h1>
              <p>{t('emv_report.subtitle')}</p>
            </div>
            <span className={`${styles.status} ${isSnapshot ? styles.snapshot : styles.live}`}>
              <ShieldCheck size={14} aria-hidden="true" />
              {isSnapshot ? t('emv_report.snapshot') : t('emv_report.live')}
            </span>
          </div>
          {formattedUpdatedAt && (
            <p className={styles.updated}>{t('emv_report.updated', { date: formattedUpdatedAt })}</p>
          )}
        </header>

        {error ? (
          <section className={styles.notice} role="alert">
            <h2>{t('emv_report.load_error')}</h2>
            <p>{error}</p>
            <div className={styles.noticeActions}>
              <Button variant="primary" onClick={loadReport} loading={loading}>
                <RefreshCw size={16} /> {t('emv_report.retry')}
              </Button>
              <Button variant="ghost" onClick={goBack}>{t('emv_report.back')}</Button>
            </div>
          </section>
        ) : (
          <>
            <section className={styles.reportCard}>
              <CreatorPerformanceCard performance={report?.performance} loading={loading} />
            </section>
            {!loading && (
              <aside className={styles.privacyNote}>
                <ShieldCheck size={18} aria-hidden="true" />
                <div>
                  <strong>{t('emv_report.privacy_title')}</strong>
                  <p>{t('emv_report.privacy_body')}</p>
                </div>
              </aside>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default CreatorEmvReport;
